import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    if (!email) return;
    setResending(true);
    try {
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        toast.error('Please wait before requesting another email.');
      } else {
        setResent(true);
        toast.success('Verification email sent!');
      }
    } catch {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="check-email-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center gap-2.5 justify-center mb-12 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/lumera-logo.png" alt="Lumera" className="w-10 h-10 rounded-full" />
          <span className="text-xl font-serif font-semibold text-foreground">Lumera</span>
        </div>

        <div className="bg-white dark:bg-card rounded-2xl shadow-sm border border-stone-100 dark:border-border p-10">
          <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#B49A88]" />
          </div>

          <h1 data-testid="check-email-title" className="text-2xl font-serif font-bold text-foreground mb-2">Check your email</h1>
          <p className="text-muted-foreground font-sans mb-2">
            We've sent a verification link to
          </p>
          {email && (
            <p data-testid="check-email-address" className="text-foreground font-semibold font-sans mb-6">
              {email}
            </p>
          )}
          <p className="text-sm text-muted-foreground font-sans mb-8">
            Click the link in the email to verify your account. The link expires in 24 hours.
          </p>

          <div className="flex flex-col gap-3">
            <Button
              data-testid="resend-verification-btn"
              variant="outline"
              onClick={handleResend}
              disabled={resending || resent}
              className="w-full rounded-full py-5 font-sans border-stone-200 hover:bg-stone-50"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : resent ? (
                <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {resent ? 'Email sent!' : 'Resend verification email'}
            </Button>

            <Button
              data-testid="check-email-continue-btn"
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-semibold px-8 py-5 rounded-full text-base"
            >
              Continue to Lumera
            </Button>
          </div>

          <p className="text-xs text-muted-foreground font-sans mt-6">
            Wrong email?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-[#B49A88] hover:underline"
            >
              Create account with a different email
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckEmail;
